import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSendNotificationMutation, useSearchUsersForNotificationQuery } from '../../../store/api/adminApi';
import { showSuccess, showError } from '../../../utils/toast';

const NotificationForm: React.FC = () => {
  const navigate = useNavigate();
  const [sendNotification, { isLoading: isSending }] = useSendNotificationMutation();
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    target_users: 'All',
    send_method: 'Both',
    specific_user_ids: [] as number[],
  });

  const [scheduleType, setScheduleType] = useState<'now' | 'custom'>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [userSearch, setUserSearch] = useState('');
  const { data: searchResults, isFetching: isSearching } = useSearchUsersForNotificationQuery(userSearch, {
    skip: userSearch.length < 2,
  });
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleAddUser = (user: any) => {
    if (!formData.specific_user_ids.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        specific_user_ids: [...prev.specific_user_ids, user.id],
      }));
      setSelectedUsers(prev => [...prev, user]);
    }
    setUserSearch('');
  };

  const handleRemoveUser = (userId: number) => {
    setFormData(prev => ({
      ...prev,
      specific_user_ids: prev.specific_user_ids.filter(id => id !== userId),
    }));
    setSelectedUsers(prev => prev.filter(user => user.id !== userId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.target_users === 'Specific' && formData.specific_user_ids.length === 0) {
      showError('Please select at least one user');
      return;
    }

    if (scheduleType === 'custom' && !scheduledAt) {
      showError('Please select a scheduled date and time');
      return;
    }

    try {
      const payload: any = {
        ...formData,
        ...(imageFile ? { image: imageFile } : {}),
        ...(scheduleType === 'custom' ? { scheduled_at: scheduledAt } : {}),
      };
      const result = await sendNotification(payload).unwrap();
      showSuccess(result.message || 'Notification queued successfully');
      navigate('/admin/notifications');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to send notification');
    }
  };

  // Compute current UAE time (Asia/Dubai = UTC+4) for the datetime-local min attribute
  const minDateTime = (() => {
    const now = new Date();
    const uae = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${uae.getFullYear()}-${pad(uae.getMonth() + 1)}-${pad(uae.getDate())}T${pad(uae.getHours())}:${pad(uae.getMinutes())}`;
  })();

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Send Notification</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary" onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer' }}>Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary" onClick={() => navigate('/admin/notifications')} style={{ cursor: 'pointer' }}>Sent Notifications</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Send Notification</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                className="btn btn-sm fw-bold btn-light"
                onClick={() => navigate('/admin/notifications')}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body py-10">
                <form onSubmit={handleSubmit} className="form">
                  {/* Title */}
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Notification Title</label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="title"
                        className="form-control form-control-lg form-control-solid"
                        placeholder="Enter notification title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Notification Body</label>
                    <div className="col-lg-8">
                      <textarea
                        name="body"
                        className="form-control form-control-lg form-control-solid"
                        rows={4}
                        placeholder="Enter notification content"
                        value={formData.body}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Image */}
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label fw-bold fs-6">Image (Optional)</label>
                    <div className="col-lg-8">
                      {imagePreview ? (
                        <div className="d-flex align-items-center gap-4">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="rounded"
                            style={{ width: 120, height: 80, objectFit: 'cover', border: '1px solid #e4e6ef' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-light-danger"
                            onClick={handleRemoveImage}
                          >
                            Remove Image
                          </button>
                        </div>
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center rounded cursor-pointer"
                          style={{
                            border: '2px dashed #e4e6ef',
                            height: 80,
                            background: '#f5f8fa',
                            cursor: 'pointer',
                          }}
                          onClick={() => imageInputRef.current?.click()}
                        >
                          <div className="text-center text-muted">
                            <i className="fas fa-image fs-2 mb-1 d-block"></i>
                            <span className="fs-7">Click to upload image</span>
                          </div>
                        </div>
                      )}
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={handleImageChange}
                      />
                      <div className="text-muted fs-7 mt-2">Recommended: JPEG or PNG, max 5MB. Shown in push notifications and emails.</div>
                    </div>
                  </div>

                  {/* Target Users */}
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Target Users</label>
                    <div className="col-lg-8">
                      <select
                        name="target_users"
                        className="form-select form-select-lg form-select-solid"
                        value={formData.target_users}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="All">All Active Users</option>
                        <option value="New">New Users (Last 30 days)</option>
                        <option value="Old">Old Users (Before 30 days)</option>
                        <option value="Specific">Specific Users</option>
                      </select>
                    </div>
                  </div>

                  {/* Specific Users Search */}
                  {formData.target_users === 'Specific' && (
                    <div className="row mb-6">
                      <label className="col-lg-4 col-form-label required fw-bold fs-6">Search & Select Users</label>
                      <div className="col-lg-8">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control form-control-lg form-control-solid"
                            placeholder="Search by name, email or phone..."
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                          />
                          {isSearching && (
                            <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                              <span className="spinner-border spinner-border-sm text-primary"></span>
                            </div>
                          )}
                          {searchResults?.data && searchResults.data.length > 0 && userSearch.length >= 2 && (
                            <div className="position-absolute w-100 bg-white shadow-sm rounded mt-1 z-index-1 overflow-auto" style={{ maxHeight: '200px' }}>
                              {searchResults.data.map((user: any) => (
                                <div
                                  key={user.id}
                                  className="p-3 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                  onClick={() => handleAddUser(user)}
                                >
                                  <div>
                                    <div className="fw-bold">{user.name}</div>
                                    <div className="text-muted fs-7">{user.email}</div>
                                  </div>
                                  <button type="button" className="btn btn-sm btn-light-primary">Add</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {selectedUsers.length > 0 && (
                          <div className="mt-5 d-flex flex-wrap gap-2">
                            {selectedUsers.map(user => (
                              <div key={user.id} className="badge badge-light-primary p-2 d-flex align-items-center gap-2">
                                <span>{user.name}</span>
                                <i
                                  className="fas fa-times cursor-pointer text-danger"
                                  onClick={() => handleRemoveUser(user.id)}
                                ></i>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Send Method */}
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Send Method</label>
                    <div className="col-lg-8">
                      <select
                        name="send_method"
                        className="form-select form-select-lg form-select-solid"
                        value={formData.send_method}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Both">Push & Email</option>
                        <option value="Push">Push Notification Only</option>
                        <option value="Email">Email Only</option>
                      </select>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Send Time</label>
                    <div className="col-lg-8">
                      <div className="d-flex gap-4 mb-4">
                        <label className="d-flex align-items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            className="form-check-input"
                            name="schedule_type"
                            value="now"
                            checked={scheduleType === 'now'}
                            onChange={() => { setScheduleType('now'); setScheduledAt(''); }}
                          />
                          <span className="fw-semibold fs-6">Send Now</span>
                        </label>
                        <label className="d-flex align-items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            className="form-check-input"
                            name="schedule_type"
                            value="custom"
                            checked={scheduleType === 'custom'}
                            onChange={() => setScheduleType('custom')}
                          />
                          <span className="fw-semibold fs-6">Schedule for Later</span>
                        </label>
                      </div>

                      {scheduleType === 'custom' && (
                        <input
                          type="datetime-local"
                          className="form-control form-control-lg form-control-solid"
                          value={scheduledAt}
                          min={minDateTime}
                          onChange={(e) => setScheduledAt(e.target.value)}
                          required={scheduleType === 'custom'}
                        />
                      )}
                    </div>
                  </div>

                  <div className="card-footer d-flex justify-content-end py-6 px-9">
                    <button
                      type="submit"
                      className="btn btn-base"
                      disabled={isSending}
                    >
                      {isSending ? (
                        <span className="indicator-label">
                          Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      ) : scheduleType === 'custom' ? 'Schedule Notification' : 'Send Notification'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationForm;
